'use client';

import { PwaPrimaryPillButton } from '@/components/pwa-ui/PwaPillButtons';
import {
  ANDROID_APK_FALLBACK_URL,
  androidApkDownloadUrl,
  androidApkFileName,
} from '@/lib/android-apk-download';
import { api } from '@/lib/api';
import { Download, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';

type ReleaseState =
  | { status: 'loading' }
  | { status: 'ready'; versionName: string; versionCode: number; downloadUrl: string; fileName: string; releaseNotes: string }
  | { status: 'error' };

export default function AndroidDownloadPage() {
  const [release, setRelease] = useState<ReleaseState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    void api
      .getAndroidAppLatest()
      .then((latest) => {
        if (!active) return;
        const fileName = androidApkFileName(latest.version_name);
        const downloadUrl = latest.download_url || androidApkDownloadUrl(latest.version_name);
        setRelease({
          status: 'ready',
          versionName: latest.version_name,
          versionCode: latest.version_code,
          downloadUrl,
          fileName,
          releaseNotes: latest.release_notes || '',
        });
      })
      .catch(() => {
        if (!active) return;
        setRelease({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, []);

  const handleDownload = () => {
    if (release.status !== 'ready') {
      window.location.href = ANDROID_APK_FALLBACK_URL;
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = release.downloadUrl;
    anchor.download = release.fileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[#F1F1F1] px-6 py-[max(2rem,env(safe-area-inset-top,0px))] dark:bg-[var(--background)]">
      <div className="mx-auto w-full max-w-md space-y-6 pt-8">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600 text-white">
            <Smartphone className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--foreground)]">Умная смена для Android</h1>
            <p className="text-sm text-[var(--muted-foreground)]">Установка APK с сайта</p>
          </div>
        </div>

        {release.status === 'loading' ? (
          <p className="text-sm text-[var(--muted-foreground)]">Загружаем информацию о версии…</p>
        ) : null}

        {release.status === 'error' ? (
          <div className="rounded-2xl border border-[var(--border)] bg-white p-4 dark:bg-[var(--surface)]">
            <p className="text-sm text-[var(--foreground)]">
              Не удалось получить версию с сервера. Можно скачать последний файл по ссылке ниже.
            </p>
            <p className="mt-2 text-xs text-[var(--muted-foreground)]">Файл: umnaya-smena.apk</p>
          </div>
        ) : null}

        {release.status === 'ready' ? (
          <div className="space-y-3 rounded-2xl border border-[var(--border)] bg-white p-4 dark:bg-[var(--surface)]">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-[var(--muted-foreground)]">Актуальная версия</span>
              <span className="text-lg font-bold text-[var(--foreground)]">v{release.versionName}</span>
            </div>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-[var(--muted-foreground)]">Код сборки</span>
              <span className="font-mono text-[var(--foreground)]">{release.versionCode}</span>
            </div>
            <div className="border-t border-[var(--border)] pt-3">
              <p className="text-xs uppercase tracking-wide text-[var(--muted-foreground)]">Имя файла при скачивании</p>
              <p className="mt-1 break-all font-mono text-sm text-[var(--foreground)]">{release.fileName}</p>
            </div>
            {release.releaseNotes ? (
              <p className="text-sm leading-relaxed text-[var(--muted-foreground)]">{release.releaseNotes}</p>
            ) : null}
          </div>
        ) : null}

        <PwaPrimaryPillButton type="button" className="w-full py-3.5" icon={Download} onClick={handleDownload}>
          {release.status === 'ready' ? `Скачать v${release.versionName}` : 'Скачать приложение'}
        </PwaPrimaryPillButton>

        <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-[var(--muted-foreground)]">
          <li>После загрузки откройте файл в Chrome или «Файлах».</li>
          <li>Разрешите установку из этого источника, если Android спросит.</li>
          <li>Установите приложение и войдите под своей учётной записью.</li>
        </ol>
      </div>
    </div>
  );
}
